import { useState, useEffect } from 'react';
import { X, Link2, Copy, Check, QrCode, Download, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import QRCode from 'qrcode';
import { trackShareCreated } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// FlipValue logo URL for QR code center
const FLIPVALUE_LOGO = 'https://i.ibb.co/5hp8HKZ/icon-192-v2.png';

// Function to add logo to QR code center
const addLogoToQR = async (qrDataUrl, logoUrl) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const qrImage = new Image();
    qrImage.crossOrigin = 'anonymous';
    qrImage.onload = () => {
      canvas.width = qrImage.width;
      canvas.height = qrImage.height;
      
      // Draw QR code
      ctx.drawImage(qrImage, 0, 0);
      
      // Load and draw logo
      const logoImage = new Image();
      logoImage.crossOrigin = 'anonymous';
      logoImage.onload = () => {
        // Logo size is 20% of QR code
        const logoSize = qrImage.width * 0.22;
        const logoX = (qrImage.width - logoSize) / 2;
        const logoY = (qrImage.height - logoSize) / 2;
        
        // Draw white circle background for logo
        ctx.beginPath();
        ctx.arc(qrImage.width / 2, qrImage.height / 2, logoSize / 2 + 4, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        
        // Draw logo
        ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
        
        resolve(canvas.toDataURL('image/png'));
      };
      logoImage.onerror = () => {
        // If logo fails to load, return original QR
        resolve(qrDataUrl);
      };
      logoImage.src = logoUrl;
    };
    qrImage.src = qrDataUrl;
  });
};

export default function ShareModal({ isOpen, onClose, inventory, categories, subscription, token }) {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  
  // Share settings
  const [title, setTitle] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [shareType, setShareType] = useState('all'); // all, selected, category
  const [selectedItems, setSelectedItems] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hideCost, setHideCost] = useState(false);
  const [hideValue, setHideValue] = useState(false);
  const [hideBranding, setHideBranding] = useState(false);
  const [hideQr, setHideQr] = useState(false);

  const tier = subscription?.tier || 'free';
  const isBasicPlus = tier === 'basic' || tier === 'pro' || tier === 'lifetime_pro';
  const isPro = tier === 'pro' || tier === 'lifetime_pro';

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setShareUrl('');
      setCopied(false);
      setQrDataUrl('');
    }
  }, [isOpen]);

  const generateShare = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/shares`, {
        title: title || null,
        custom_message: isBasicPlus ? customMessage : null,
        selected_items: shareType === 'selected' ? selectedItems : [],
        share_type: shareType,
        category_filter: shareType === 'category' ? categoryFilter : null,
        hide_cost: isBasicPlus ? hideCost : false,
        hide_value: isBasicPlus ? hideValue : false,
        hide_branding: isPro ? hideBranding : false,
        hide_qr: isPro ? hideQr : false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Use current origin for share URL (works in dev and production)
      const shareCode = response.data.share_code;
      const url = `${window.location.origin}/share/${shareCode}`;
      setShareUrl(url);
      
      // Generate QR code with logo
      const qrBase = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        errorCorrectionLevel: 'H', // High error correction to support logo overlay
        color: { dark: '#10B981', light: '#ffffff' }
      });
      
      // Add FlipValue logo to center
      const qrWithLogo = await addLogoToQR(qrBase, FLIPVALUE_LOGO);
      setQrDataUrl(qrWithLogo);
      
      trackShareCreated('inventory');
      toast.success('Share link created!');
    } catch (error) {
      console.error('Failed to create share:', error);
      toast.error('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const link = document.createElement('a');
    link.download = 'flipvault-share-qr.png';
    link.href = qrDataUrl;
    link.click();
    toast.success('QR code downloaded!');
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
          <h2 className="text-lg font-bold">Share Inventory</h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {!shareUrl ? (
            <>
              {/* Title */}
              <div>
                <label className="text-sm font-medium mb-1 block">Title (optional)</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My Collection"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>

              {/* Share Type */}
              <div>
                <label className="text-sm font-medium mb-2 block">What to share</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setShareType('all')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      shareType === 'all' 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:bg-secondary'
                    }`}
                  >
                    All Items
                  </button>
                  <button
                    onClick={() => setShareType('selected')}
                    disabled={!isBasicPlus}
                    className={`px-3 py-2 rounded-lg border text-sm relative ${
                      shareType === 'selected' 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:bg-secondary'
                    } ${!isBasicPlus ? 'opacity-50' : ''}`}
                  >
                    Selected
                    {!isBasicPlus && <Lock className="w-3 h-3 absolute top-1 right-1" />}
                  </button>
                  <button
                    onClick={() => setShareType('category')}
                    disabled={!isBasicPlus}
                    className={`px-3 py-2 rounded-lg border text-sm relative ${
                      shareType === 'category' 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-border hover:bg-secondary'
                    } ${!isBasicPlus ? 'opacity-50' : ''}`}
                  >
                    Category
                    {!isBasicPlus && <Lock className="w-3 h-3 absolute top-1 right-1" />}
                  </button>
                </div>
              </div>

              {/* Category Selection */}
              {shareType === 'category' && isBasicPlus && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  >
                    <option value="">Choose a category...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Item Selection */}
              {shareType === 'selected' && isBasicPlus && (
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Select Items ({selectedItems.length} selected)
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                    {inventory.map(item => (
                      <label 
                        key={item.id} 
                        className="flex items-center gap-2 p-2 hover:bg-secondary rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                          className="rounded"
                        />
                        <span className="text-sm truncate">{item.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacy Toggles */}
              <div className="space-y-3 pt-2">
                <p className="text-sm font-medium">Privacy Options</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Hide cost/price paid</span>
                    {!isBasicPlus && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch 
                    checked={hideCost} 
                    onCheckedChange={setHideCost}
                    disabled={!isBasicPlus}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Hide current value/profit</span>
                    {!isBasicPlus && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch 
                    checked={hideValue} 
                    onCheckedChange={setHideValue}
                    disabled={!isBasicPlus}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Hide FlipValue branding</span>
                    {!isPro && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch 
                    checked={hideBranding} 
                    onCheckedChange={setHideBranding}
                    disabled={!isPro}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Hide QR code</span>
                    {!isPro && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch 
                    checked={hideQr} 
                    onCheckedChange={setHideQr}
                    disabled={!isPro}
                  />
                </div>
              </div>

              {/* Custom Message (Basic+) */}
              {isBasicPlus && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Custom Message (optional)</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Add a personal note..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background resize-none"
                  />
                </div>
              )}

              {/* Tier Info */}
              {tier === 'free' && (
                <p className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded-lg">
                  Free plan: Share all items with cost, value, and branding visible. 
                  Upgrade for more options!
                </p>
              )}

              <Button 
                onClick={generateShare} 
                disabled={loading || (shareType === 'selected' && selectedItems.length === 0) || (shareType === 'category' && !categoryFilter)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {loading ? 'Creating...' : 'Generate Share Link'}
              </Button>
            </>
          ) : (
            <>
              {/* Share URL Result */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Link2 className="w-8 h-8 text-primary" />
                </div>
                <p className="font-medium">Your share link is ready!</p>
                
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm"
                  />
                  <Button onClick={copyLink} variant="outline" size="sm">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>

                {/* QR Code */}
                {qrDataUrl && (
                  <div className="flex flex-col items-center gap-2">
                    <img src={qrDataUrl} alt="QR Code" className="w-40 h-40 rounded-lg" />
                    <Button onClick={downloadQR} variant="outline" size="sm" className="gap-2">
                      <Download className="w-4 h-4" />
                      Download QR
                    </Button>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={() => setShareUrl('')} 
                    variant="outline" 
                    className="flex-1"
                  >
                    Create Another
                  </Button>
                  <Button 
                    onClick={() => window.open(shareUrl, '_blank')} 
                    className="flex-1 bg-primary"
                  >
                    Preview
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
